import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { type ReactNode } from 'react'
import {
  Button,
  Icon,
  type IIconsVector,
  Switch,
  Tooltip,
  type ITooltipProps,
  Typography,
} from 'shared/ui'
import styles from './styles.module.scss'

type IAddOnCardProps = {
  icon: IIconsVector
  iconFontSize?: number
  title: string
  titleAdditionalContent?: ReactNode
  price: string
  description: string
  isActive: boolean
  isSwitchDisabled?: boolean
  tooltipProps?: ITooltipProps
  showSwitch?: boolean
  actionButtonText?: string
  onSwitchChange?: (value: boolean) => void
  onActionClick?: () => void
  onSeeDetailsClick?: () => void
}

export const AddOnCard = observer(
  ({
    icon,
    iconFontSize = 20,
    title,
    titleAdditionalContent,
    price,
    description,
    isActive = false,
    isSwitchDisabled = false,
    tooltipProps,
    showSwitch = true,
    actionButtonText,
    onSwitchChange,
    onActionClick,
    onSeeDetailsClick,
  }: IAddOnCardProps) => {
    return (
      <div className={classNames(styles.infoContainer, isActive && styles.infoContainerActive)}>
        <div className={styles.iconContainer}>
          <Icon
            icon={icon}
            fontSize={iconFontSize}
            color={isActive ? 'var(--content-brand-primary)' : 'var(--content-primary-tertiary)'}
          />
        </div>

        <div className={styles.infoContainerData}>
          <Typography ariaLabel={'sectionTitle'} tag='div' className={styles.sectionTitle}>
            <Typography
              ariaLabel={'sectionTitleText'}
              className={styles.sectionTitleText}
              variant='body-lg-medium'
            >
              {title}
            </Typography>
            {titleAdditionalContent}
          </Typography>
          <Typography
            ariaLabel={'price'}
            variant='body-md-regular'
            className={styles.infoContainerDataText}
          >
            {price}
          </Typography>

          <Typography
            ariaLabel={'description'}
            variant='body-md-regular'
            color='var(--content-primary-secondary)'
          >
            {description}
          </Typography>
        </div>

        <div className={styles.switchContainer}>
          <div className={styles.switchContainerButtons}>
            {onActionClick && (
              <Button
                text={actionButtonText ?? (isActive ? 'Go to settings' : 'Register')}
                typeBtn='contained'
                size='small'
                className={styles.actionButton}
                onClick={onActionClick}
              />
            )}
            {showSwitch &&
              (tooltipProps ? (
                <Tooltip placement={tooltipProps.placement || 'top'} {...tooltipProps}>
                  <Switch
                    ariaLabel='switch'
                    size='middle'
                    value={isActive}
                    disabled={isSwitchDisabled}
                    onChange={onSwitchChange}
                  />
                </Tooltip>
              ) : (
                <Switch
                  ariaLabel='switch'
                  size='middle'
                  value={isActive}
                  disabled={isSwitchDisabled}
                  onChange={onSwitchChange}
                />
              ))}
          </div>
          {onSeeDetailsClick && (
            <Typography
              ariaLabel='addon_card_see_details'
              variant='body-md-medium'
              className={styles.seeDetailsLink}
              onClick={onSeeDetailsClick}
            >
              See details
            </Typography>
          )}
        </div>
      </div>
    )
  }
)
